import React, {
  PropsWithChildren,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import FocusTrap from 'focus-trap-react';
import styled, { css, keyframes } from 'styled-components';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import colors from '../common/theme/colors';
import constants from '../common/theme/constants';
import { fadeIn, fadeOut } from '../common/animations';
import { EventName, TranslateFunc } from '../common/types';
import { updateBodyScrollState } from '../common/utils/KitUI';
import { focusTrapDelay } from '../common/utils/KitCommon';
import KitCloseButton from './KitCloseButton';
import KitToast from './KitToast';
import useMediaQuery from '../common/hooks/useMediaQuery';
import useWindowSize from '../common/hooks/useWindowSize';

const FocusTrapTyped = FocusTrap as unknown as React.FC<PropsWithChildren<FocusTrap.Props>>;

const modalContentOut = keyframes`
   0% {
       opacity: 1;
       transform: translateY(0);
   }
   100% {
        opacity: 0;
        transform: translateY(-25%);
   }
`;

const modalContentIn = keyframes`
   0% {
       opacity: 0;
       transform: translateY(-25px);
   }
   100% {
        opacity: 1;
        transform: translateY(0);
   }
`;

const ModalContainer = styled.div<{
  zIndex?: number;
}>`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.font.fontFamily};
  z-index: ${({ zIndex }) => zIndex ?? constants.zIndex.modal};
  && {
    &.out {
      .modal-capture {
        animation-delay: ${constants.duration.short}s;
        animation: ${fadeOut} ${constants.duration.medium}s forwards;
      }
      .cp-modal-content {
        animation: ${modalContentOut} ${constants.duration.medium}s cubic-bezier(0, 1, 0.5, 1);
      }
    }

    &.in {
      .modal-capture {
        animation: ${fadeIn} ${constants.duration.medium}s forwards;
      }
      .cp-modal-content {
        animation: ${modalContentIn} ${constants.duration.medium}s cubic-bezier(0, 1, 0.5, 1)
          forwards;
      }
    }
  }
`;

const BackDrop = styled.div`
  && {
    opacity: 0;
    position: absolute;
    background: ${colors.black}66;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }
`;

const ModalContent = styled.div<{
  size?: string;
  customStyles?: string;
  position?: string;
  positionOffset?: number;
  windowHeight: number;
}>`
  background: ${({ theme }) => theme.card.body.bg};
  display: flex;
  flex-direction: column;
  margin: ${({ position }) =>
    position === 'center' ? '0 auto' : `${constants.spacing.absolute.l}px auto`};
  max-height: ${({ windowHeight }) => `${windowHeight - constants.spacing.absolute.l * 2}px`};
  max-width: calc(100vw - ${constants.spacing.absolute.l * 2}px);
  opacity: 0;
  position: relative;
  transition: all 0.3s;
  width: auto;
  ${({ positionOffset }) =>
    positionOffset &&
    css`
      top: ${positionOffset}px;
      left: ${positionOffset}px;
    `}

  &.shadow {
    box-shadow: 2px 2px 6px 2px ${colors.black}33;
  }

  &.xs {
    width: 250px;
  }

  &.sm {
    width: 350px;
  }

  &.md {
    width: 500px;
  }
  &.lg {
    width: 800px;
  }

  &.full {
    margin: ${constants.spacing.absolute.s}px auto;
    min-width: inherit;
    max-width: calc(100vw - ${constants.spacing.absolute.m}px);
  }

  @media all and (max-width: ${constants.breakpoints.sm}px) {
    width: auto;
    max-width: calc(100vw - ${constants.spacing.absolute.xs * 2}px);
    min-width: inherit;
    max-height: ${({ windowHeight }) => `${windowHeight - constants.spacing.absolute.xs * 2}px`};
    margin: ${constants.spacing.absolute.xs}px;
  }

  ${({ customStyles }) => (customStyles ? `${customStyles}` : '')};
`;

const ModalHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.modal.separator};
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
  padding: ${constants.spacing.m}rem;
`;

const ModalBody = styled.div`
  padding: ${constants.spacing.m}rem;
  overflow: auto;
  max-height: inherit;
`;

const ModalFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.modal.separator};
  padding: ${constants.spacing.s}rem ${constants.spacing.m}rem;
`;

const ModalTitle = styled.h4`
  flex: 1;
  line-height: ${constants.fontSize.h4}rem;
  margin-bottom: 0;
  margin-top: 0;
`;

export interface HeaderProps extends PropsWithChildren {
  closeButton?: boolean;
  t: TranslateFunc;
}

export interface TitleProps {
  children: ReactNode;
  id?: string;
}

const createElement = (): HTMLDivElement => {
  let el = document.querySelector('.cp-toolkit-modal') as HTMLDivElement;
  if (!el) {
    el = document.createElement('div');
    el.classList.add('cp-toolkit-modal');
    document.body.appendChild(el);
  }
  return el;
};

const ModalMount = ({ children }) => {
  const allDialogs = document.querySelectorAll<HTMLElement>('.cp-modal-container');

  if (allDialogs.length) {
    allDialogs.forEach((el, index) => {
      el.setAttribute('aria-hidden', index !== allDialogs.length - 1 ? 'true' : 'false');
    });
  }
  const container: HTMLDivElement = createElement();
  return createPortal(children, container);
};

export enum KitModalSize {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  full = 'full',
}

export interface KitModalProps {
  ariaLabelledBy?: string;
  alertTextId?: string;
  checkCanFocusTrap?: (containers?: (HTMLElement | SVGElement)[]) => Promise<void>;
  children?: ReactNode;
  closeOnClickOutside?: boolean;
  customStyles?: string;
  dialogClassName?: string;
  show?: boolean;
  size?: KitModalSize | number;
  shadow?: boolean;
  onHide?: () => void;
  onInitComplete?: () => void;
  setReturnFocus?:
    | HTMLElement
    | SVGElement
    | string
    | false
    | ((nodeFocusedBeforeActivation: HTMLElement | SVGElement) => HTMLElement | SVGElement | false);
  initialFocus?:
    | HTMLElement
    | SVGElement
    | string
    | false
    | (() => HTMLElement | SVGElement | false);
  skipReturnFocusOnDeactivate?: boolean;
  animation?: boolean;
  position?: 'center' | 'default';
  // we'll need a toastContainerId to put toast container inside a dialog,
  // otherwise toast won'tbe announced by a screenreader when invoked in a dialog.
  toastContainerId?: string;
  zIndex?: number;
}

type KitModalDef = React.FC<KitModalProps> & {
  Header: React.FC<HeaderProps>;
  Body: React.FC<PropsWithChildren>;
  Footer: React.FC<PropsWithChildren>;
  Title: React.FC<TitleProps>;
};

const KitModal: KitModalDef = (props: KitModalProps) => {
  const {
    animation = true,
    ariaLabelledBy,
    alertTextId,
    checkCanFocusTrap,
    closeOnClickOutside = true,
    children,
    customStyles,
    onHide,
    onInitComplete,
    position,
    show,
    size = KitModalSize.md,
    shadow,
    setReturnFocus,
    initialFocus,
    skipReturnFocusOnDeactivate,
    toastContainerId,
    zIndex,
  } = props;
  const [transition, setTransition] = useState('');
  const modalClass = 'cp-toolkit-modal';
  const propsWithDefaults: Partial<KitModalProps> = {
    // injected props will override default props set above
    ...props,
    // override props
    // always add the 'cp-modal' class
    dialogClassName: classNames(modalClass, props.dialogClassName),
  };
  const { height: windowHeight } = useWindowSize();
  const contentRef: RefObject<HTMLDivElement> = useRef();
  const containerRef: RefObject<HTMLDivElement> = useRef();

  const sizeClass: string = typeof size === 'number' ? 'custom' : size;

  // We should AWLAYS pass canCheckFocusTrap.  In newer versions of react-focus-trap:
  // - IF there is no focusable element
  //    AND canCheckFocusTrap is NOT passed
  //    THEN an exception is thrown
  const fallbackCanCheckFocus = focusTrapDelay(50);

  const updateLayout = useCallback(() => {
    if (position === 'center' && contentRef.current) {
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      const ch = contentRef.current.getBoundingClientRect().height;
      const cy = Math.max(vh / 2 - ch / 2, 0);

      contentRef.current.style.top = `${cy}px`;
    }
  }, [contentRef, position]);

  // logic to layer multiple modals slightly down and to the right for better visibility
  const isSmallScreen = useMediaQuery(`(max-width: ${constants.breakpoints.sm}px)`);
  let modalOffsetBump = 0;
  const modals = document.querySelectorAll('.cp-toolkit-modal > .cp-modal-container');

  if (!isSmallScreen) {
    // eslint-disable-next-line no-plusplus
    for (let x = 0; x < modals.length; x++) {
      if (modals[x] === containerRef.current) {
        modalOffsetBump = x * 10;
      }
    }
  }

  const hideModal = useCallback(() => {
    setTransition('out');
    updateBodyScrollState();
    setTimeout(
      () => {
        if (onHide) {
          onHide();
        }
      },
      animation ? 100 : 0
    );
  }, [animation, onHide]);

  const onKeyDown = useCallback(
    (e) => {
      if (['Escape', 'Esc'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        if (closeOnClickOutside) {
          hideModal();
        }
      }
    },
    [closeOnClickOutside, hideModal]
  );

  const onClickCapture = (e) => {
    const { target } = e;
    if (target.classList && target.classList.contains('modal-close')) {
      hideModal();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleClickOutside = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (closeOnClickOutside) {
      hideModal();
    }
  };

  const addListeners = useCallback(() => {
    window.addEventListener(EventName.RESIZE, updateLayout);
  }, [updateLayout]);

  const cleanup = useCallback(() => {
    updateBodyScrollState();
    const allDialogs = document.querySelectorAll<HTMLElement>('.cp-modal-container');
    if (allDialogs.length) {
      allDialogs[allDialogs.length - 1]?.setAttribute('aria-hidden', 'false');
      window.removeEventListener(EventName.RESIZE, updateLayout);
    }
  }, [updateLayout]);

  useEffect(() => {
    if (show) {
      setTransition('in');
      setTimeout(() => {
        updateBodyScrollState();
        updateLayout();

        if (onInitComplete) {
          onInitComplete();
        }
      }, 20);

      addListeners();
    } else {
      cleanup();
    }

    return () => {
      cleanup();
    };
  }, [addListeners, cleanup, show, onInitComplete, updateLayout]);

  return (
    <ModalMount>
      {show &&
        ((
          <FocusTrapTyped
            focusTrapOptions={{
              checkCanFocusTrap: checkCanFocusTrap ?? fallbackCanCheckFocus,
              allowOutsideClick: true,
              setReturnFocus,
              initialFocus,
              returnFocusOnDeactivate: !skipReturnFocusOnDeactivate,
              fallbackFocus: '.cp-modal-container',
              escapeDeactivates: closeOnClickOutside,
            }}
          >
            <ModalContainer
              role={alertTextId ? 'alertdialog' : 'dialog'}
              aria-labelledby={ariaLabelledBy ?? 'cp-modal-title'}
              aria-describedby={alertTextId}
              aria-modal='true'
              className={classNames(
                'cp-modal-container',
                propsWithDefaults.dialogClassName,
                transition
              )}
              onClickCapture={onClickCapture}
              ref={containerRef}
              onKeyDown={onKeyDown}
              zIndex={zIndex}
            >
              <>
                <BackDrop className='modal-capture' onClick={handleClickOutside} />
                {!!toastContainerId && (
                  <KitToast
                    position='top-right'
                    containerId={toastContainerId}
                    duration={8000}
                    reverseOrder
                  />
                )}
                <ModalContent
                  ref={contentRef}
                  customStyles={customStyles}
                  className={classNames('cp-modal-content', sizeClass, { shadow })}
                  position={position}
                  positionOffset={modalOffsetBump}
                  windowHeight={windowHeight}
                >
                  {children}
                </ModalContent>
              </>
            </ModalContainer>
          </FocusTrapTyped>
        ) as ReactNode)}
    </ModalMount>
  );
};

KitModal.Header = ({ children, closeButton, t }) => (
  <ModalHeader data-id='modal-header'>
    {children}
    {closeButton && (
      <KitCloseButton
        data-testid='modal-close'
        className='modal-close'
        aria-label={t('kit:button.close')}
      />
    )}
  </ModalHeader>
);

KitModal.Title = (props: TitleProps) => <ModalTitle id={props.id || 'cp-modal-title'} {...props} />;
KitModal.Body = (props) => <ModalBody data-id='modal-body' {...props} />;
KitModal.Footer = (props) => <ModalFooter data-id='modal-footer' {...props} />;

export default KitModal;
